import React from "react";

type TimelineRange = {
  start: number;
  end: number;
};

interface ScriptClipSectionSelectionProps {
  duration: number;
  range: TimelineRange | null;
  variant?: "scene" | "transcript" | "tool";
  interactive?: boolean;
}

export type ScriptClipSelectionChrome = "scene" | "transcript" | "tool";

/**
 * Only one selection language should be visible at a time. A precise user
 * transcript selection supersedes structural scene chrome, while the range
 * tool supersedes both.
 */
export const resolveScriptClipSelectionChrome = ({
  rangeToolEnabled,
  hasTranscriptSelection,
}: {
  rangeToolEnabled: boolean;
  hasTranscriptSelection: boolean;
}): ScriptClipSelectionChrome => {
  if (rangeToolEnabled) return "tool";
  if (hasTranscriptSelection) return "transcript";
  return "scene";
};

const EDGE_TOLERANCE_SEC = 1e-3;
const OUTER_CORNER_RADIUS_PX = 6;

/**
 * Passive scene/section selection chrome for the compound script clip.
 *
 * WaveSurfer deliberately does not own this element. It remains a renderer,
 * while this full-height overlay owns scene, transcript, and range chrome.
 */
const ScriptClipSectionSelection: React.FC<ScriptClipSectionSelectionProps> = ({
  duration,
  range,
  variant = "scene",
  interactive = false,
}) => {
  if (!range || !Number.isFinite(duration) || duration <= 0) return null;

  const rawStart = Number(range.start);
  const rawEnd = Number(range.end);
  if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd)) return null;

  const start = Math.max(0, Math.min(duration, Math.min(rawStart, rawEnd)));
  const end = Math.max(0, Math.min(duration, Math.max(rawStart, rawEnd)));
  if (end - start <= EDGE_TOLERANCE_SEC) return null;

  const touchesStart = start <= EDGE_TOLERANCE_SEC;
  const touchesEnd = duration - end <= EDGE_TOLERANCE_SEC;

  return (
    <div
      data-testid={`script-clip-${variant}-selection`}
      className={`script-clip__section-selection script-clip__section-selection--${variant}`}
      data-range-action={interactive ? "move" : undefined}
      style={{
        left: `${(start / duration) * 100}%`,
        width: `${((end - start) / duration) * 100}%`,
        borderTopLeftRadius: touchesStart ? OUTER_CORNER_RADIUS_PX : 0,
        borderBottomLeftRadius: touchesStart ? OUTER_CORNER_RADIUS_PX : 0,
        borderTopRightRadius: touchesEnd ? OUTER_CORNER_RADIUS_PX : 0,
        borderBottomRightRadius: touchesEnd ? OUTER_CORNER_RADIUS_PX : 0,
      }}
    >
      {interactive && (
        <>
          <span
            aria-label="Resize range start"
            className="script-clip__range-handle script-clip__range-handle--start"
            data-range-action="resize-start"
          />
          <span
            aria-label="Resize range end"
            className="script-clip__range-handle script-clip__range-handle--end"
            data-range-action="resize-end"
          />
        </>
      )}
    </div>
  );
};

export default ScriptClipSectionSelection;
